import { ResourceType } from '@cotera/api';
import type { PendingChatSubmission } from '../chat/models/pending-chat-submission';
import { getChatSeedStrategyForResourceType } from '../chat-seed';
import {
  RESOURCE_TYPE_CONFIG,
  type NonFolderResourceType,
} from '../resources/type/resource-type-config';

export type ResourceRef = { type: NonFolderResourceType; id: string };

const RESOURCE_TYPE_BY_ROUTE_SEGMENT = new Map<string, NonFolderResourceType>(
  Object.entries(RESOURCE_TYPE_CONFIG).flatMap(([type, config]) => {
    const resourceType = type as NonFolderResourceType;
    return [
      [config.itemRouteSegment, resourceType] as const,
      [config.listRouteSegment, resourceType] as const,
    ];
  })
);

/**
 * `/{orgSlug}/{segment}/{uuid}` (or the org-less form) → the resource it points
 * at, e.g. `/acme/workflows/<uuid>` → a Workflow ref.
 */
export function extractResourceRefFromPath(path: string): ResourceRef | null {
  const matched = path.match(
    /^(?:\/[^/]+)?\/([^/]+)\/([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i
  );
  const segment = matched?.[1];
  const id = matched?.[2];
  if (segment === undefined || id === undefined) {
    return null;
  }
  const type = RESOURCE_TYPE_BY_ROUTE_SEGMENT.get(segment.toLowerCase());
  if (type === undefined) {
    return null;
  }
  return { type, id };
}

function toResourceRef(target: {
  type: ResourceType | string;
  id: string;
}): ResourceRef | null {
  const type = target.type as NonFolderResourceType;
  if (RESOURCE_TYPE_CONFIG[type] === undefined) {
    return null;
  }
  return { type, id: target.id };
}

/**
 * The artifact a `chat` post-activation step should seed the new chat with:
 * the navigate step's target when it names a seedable resource, otherwise the
 * shared resource itself.
 */
export function resolveChatSeedRef(
  path: string,
  target: { type: ResourceType | string; id: string }
): ResourceRef | null {
  const candidates = [extractResourceRefFromPath(path), toResourceRef(target)];
  for (const candidate of candidates) {
    if (
      candidate !== null &&
      getChatSeedStrategyForResourceType(candidate.type) !== undefined
    ) {
      return candidate;
    }
  }
  return null;
}

/**
 * Context attached to the chat a `chat` step opens, so the shared resource is
 * actually present in it. Agents are attached as an invocable sub-agent — a
 * shared agent link starts a chat *with* the agent, not one editing it —
 * while every other seedable type (workflows today) opens in the artifact
 * panel with its editor tool loaded, matching that resource's normal entry
 * point.
 */
export function buildChatSeedSubmission(
  seedRef: ResourceRef | null
): Partial<PendingChatSubmission> {
  if (seedRef === null) {
    return {};
  }
  if (seedRef.type === ResourceType.Agent) {
    return {
      agentId: seedRef.id,
      initialActions: [{ type: 'focus_agent', agentId: seedRef.id }],
    };
  }
  const strategy = getChatSeedStrategyForResourceType(seedRef.type);
  if (strategy === undefined) {
    return {};
  }
  return strategy.buildPendingContext({ artifactId: seedRef.id });
}
